import {useContext, useState} from 'react';
import * as React from 'react';
import {modalStore, StoreContext} from "../stores/StoreLoader";
import {observer} from "mobx-react";
import PackeryGrid from "../components/grids/PackeryGrid";
import {gridTypesEnum, makeNounPossessive} from "../utils/SchoolBlocksUtilities";
import {ToggleOrganizationPrivacyContainer} from "../components/misc/ToggleOrganizationPrivacyContainer";
import Breadcrumbs from "../components/misc/Breadcrumbs";
import FollowButton from "../components/misc/FollowButton";
import styles from "./styles/MainGridPage.module.scss";
import LoginButtons from "../components/admin/sidebar/items/LoginButtons";
import {Microsite, useMicrosite} from "../components/header/microsite/Microsite";
import classNames from 'classnames';
import {getBackgroundImageValue} from "../stores/StyleStore";
import ClickableLink from "../components/utilities/ClickableLink";
import LoginBox from "../components/misc/LoginBox";
import FontAwesome from "../components/utilities/FontAwesome";
import {MicrositeToggle} from "../components/header/microsite/MicrositeToggle";
import {TerracedGrid} from "../components/grids/TerracedGrid";
import {blockObjectFactory} from "../components/blocks/SchoolBlocks/blockUtils";

export interface IBreadcrumb {
    isPrivate: boolean,
    type: "department" | "group" | "team" | "section" | "classroom" | "page",
    label: string,
    url: string,
    model?: {
        id?: string,
    },
}

const MainGridPage: React.FC<{
    blocks: AnyBlockObj[],
    teachers?: IUser[],
    currentOrganization: OrganizationTypeOrganization,
}> = observer(props => {
    const {userStore, organizationStore} = useContext(StoreContext);

    const orgType = organizationStore.currentOrganization.type
    const isClassroom = orgType === "classroom";
    const [showGrid, setShowGrid] = useState(isClassroom ? !!userStore.id : true);

    const gridPage = orgType === "group" || orgType === "section" || orgType === "department" || orgType === "team";
    const mainPage = orgType === "school" || orgType === "district" || orgType === "organization";

    const {shouldShowMicrosite, canShowMicrosite, microsite} = useMicrosite();
    const [micrositeOpen, setMicrositeOpen] = useState(false);

    const isTerracedGrid = !!organizationStore.currentOrganization.json_data.terracedGrid?.enabled;

    const gridClassName = classNames({
        "sb-content-width": true,
        "sb-grid-with-microsite": shouldShowMicrosite,
        [styles.grid]: true,
        [styles.gridWithMicrosite]: shouldShowMicrosite,
        [styles.gridGridPage]: gridPage || isClassroom,
        [styles.fullWidthPage]: isTerracedGrid,
    })
    const micrositeClassName = classNames({
        [styles.microsite]: true,
        [styles.micrositeOpen]: micrositeOpen,
    })
    const loginWrapperClassName = classNames({
        "'sb-main-content": true,
        [styles.loginWrapper]: true,
    })
    const gridContainerClassName = classNames({
        "sb-main-content": true,
        "packery": !isTerracedGrid,
    });
    const containerClassName = classNames({
        [styles.container]: true,
    })

    const teacherCount = props.teachers?.length || 0;
    const classroomType = organizationStore.currentOrganization.json_data?.settings?.linkedClassroomType;
    const isLinkedClassroom = !!organizationStore.currentOrganization.json_data?.settings?.linkedClassroomId;

    const header = <div className={styles.header}>
        <div className={styles.classroomHeaderTopBlock}>
            <div>
                {shouldShowMicrosite && <button className={styles.micrositeToggleButton} onClick={() => setMicrositeOpen(v => !v)}>
                    <FontAwesome name={"fa-bars"} size={"2x"} prefix={'fas'}/>
                </button>}
                <Breadcrumbs breadcrumbs={organizationStore.currentOrganization.breadcrumbs} hasMicrosite={shouldShowMicrosite} />
            </div>
            <div className={styles.rightAlignedHeader}>
                {userStore.editor && canShowMicrosite && microsite.id === organizationStore.currentOrganization.id && <MicrositeToggle />}
                {isClassroom && <div>
                    <div className={styles.classroomTeacher}>
                        {props.teachers?.map(teacher => {
                            const modalObj = {
                                id: teacher.id,
                                type: "schoolBlocksPerson",
                                blockObj: blockObjectFactory("person", {id: teacher.id, blockModel: teacher}),
                            }

                            return <div key={teacher.id} className={styles.classroomMultipleTeachers}>
                                <button onClick={() => modalStore.addModal(modalObj)}>
                                    <div className={`${styles.classroomTeacherImage} sb-circular-profile-image`}
                                         role="presentation" aria-hidden="true"
                                         title={`${teacher.fname} ${teacher.lname}`}
                                         style={getBackgroundImageValue(teacher.avatar || require("../assets/images/person-placeholder.jpg"))}>
                                    </div>
                                    <div>
                                        {`${teacher.fname} ${teacher.lname}`}
                                    </div>
                                </button>
                            </div>
                        })}
                    </div>
                </div>}
                {isClassroom ? <div className={`${styles.classroomHeaderLabels} sb-main-content`}>
                    {isLinkedClassroom &&
                        <ClickableLink href={organizationStore.currentOrganization.json_data.settings.linkedClassroomUrl}
                                       className={`btn btn-default ${styles.classroomLinkedFloatingButton}`}
                                       role="button" title={"Link to Google Classroom"}>
                            {classroomType === "google" && <>
                                <img alt={"Google Classroom Icon"} src={require("../assets/images/google_classroom_green.png")}/>
                                <div className={styles.hiddenWhenSmall}>Google Class</div></>}
                        </ClickableLink>}
                </div> : <span id={"privateOrganizationButton"}>
                    <ToggleOrganizationPrivacyContainer
                            key={organizationStore.currentOrganization.is_private}
                            orgId={organizationStore.currentOrganization.id}
                            orgType={orgType}
                            isPrivate={organizationStore.currentOrganization.is_private}
                            isEditable={userStore.editor}
                        />
                </span>}
                <div className={`${styles.classroomHeaderLabels} sb-main-content`}>
                    <FollowButton/>
                </div>
            </div>
        </div>
    </div>

    return <div className={containerClassName}>
        <div id={`sb-main-content`} className={gridClassName}>
            {(gridPage || isClassroom) && header}
            {shouldShowMicrosite && <div className={micrositeClassName}>
                <Microsite setMicrositeOpen={setMicrositeOpen} micrositeOpen={micrositeOpen}/>
            </div>}
            {mainPage || (gridPage && userStore.viewer) || (isClassroom && showGrid) ?
                <div className={`sb-grid-wrapper ${styles.packery}`}>
                    <div id="main-grid-container" className={gridContainerClassName}
                         data-grid-perms={userStore.editor ? 1 : 0}>
                        {organizationStore.currentOrganization.json_data.terracedGrid?.enabled ? <TerracedGrid blocks={props.blocks} /> :
                            <PackeryGrid
                            hasMicrosite={shouldShowMicrosite}
                            gridType={gridTypesEnum.FLEX_GRID}
                            htmlId={'main-grid'}
                            key={props.currentOrganization.id}
                            {...props}
                        />}
                    </div>
                </div> : <>
                    {isClassroom ? <div className={loginWrapperClassName}>
                        <LoginBox
                            customHeader={'Login to see the classroom page.'}
                            handleSkipLogin={() => setShowGrid(true)}
                        /></div> : <div className="sb-content-width">
                            <div className="sb-error-block">
                                <div className="sb-error-message">
                                    <h3 className="text-danger">
                                        <b>This is a private section that you do not have permission to view.</b>
                                    </h3>
                                    {!!userStore.id || <div style={{marginTop: "1rem"}}>
                                        <LoginButtons/>
                                    </div>}
                                </div>
                            </div>
                        </div>}
                </>}
        </div>
    </div>
});

export default MainGridPage;
